import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Instagram, Youtube, Play } from "lucide-react";
import { format } from "date-fns";
import { Skeleton } from "@/components/ui/skeleton";
import { useEffect } from "react";

interface SocialCampaignsProps {
  northId: string;
  address: string;
  forceMobileLayout?: boolean;
}

const SocialCampaigns = ({ northId, address, forceMobileLayout = false }: SocialCampaignsProps) => {
  const queryClient = useQueryClient();
  const isDemo = northId === 'aaaaaaaa-0000-0000-0000-000000000001';
  const targetNorthId = isDemo ? 'd0daa852-5636-45c6-8a29-0d12ba9cf2d1' : northId;

  const { data: posts, isLoading } = useQuery({
    queryKey: ["social-campaigns", northId, "v5"], // v5: includes direct north_id posts for backward compat
    queryFn: async () => {
      // Fetch posts via junction table (new architecture)
      const { data: junctionData } = await supabase
        .from("north_social_posts")
        .select("social_post_id")
        .eq("north_id", targetNorthId);

      // Fetch posts added directly to this North (legacy approach, pre-junction table)
      const { data: directData } = await supabase
        .from("social_media_posts")
        .select("*, account:social_media_accounts(account_name, platform)")
        .eq("north_id", targetNorthId)
        .eq("is_added_to_report", true);

      const seenIds = new Set<string>();
      const allPosts: any[] = [];

      // Add junction posts first
      if (junctionData && junctionData.length > 0) {
        const postIds = junctionData.map((item: any) => item.social_post_id);
        const { data: junctionPosts } = await supabase
          .from("social_media_posts")
          .select("*, account:social_media_accounts(account_name, platform)")
          .in("id", postIds)
          .order("posted_at", { ascending: false });
        for (const p of junctionPosts || []) {
          if (!seenIds.has(p.id)) { seenIds.add(p.id); allPosts.push(p); }
        }
      }

      // Add direct posts (legacy), deduplicating
      for (const p of directData || []) {
        if (!seenIds.has(p.id)) { seenIds.add(p.id); allPosts.push(p); }
      }

      allPosts.sort((a, b) => new Date(b.posted_at).getTime() - new Date(a.posted_at).getTime());
      return allPosts;
    },
  });

  // Set up real-time subscription to listen for changes to north_social_posts
  useEffect(() => {
    const channel = supabase
      .channel(`north-social-posts-${targetNorthId}`)
      .on(
        'postgres_changes',
        {
          event: '*', // Listen for INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'north_social_posts',
          filter: `north_id=eq.${targetNorthId}`,
        },
        () => {
          // Invalidate and refetch when junction table changes
          queryClient.invalidateQueries({ queryKey: ["social-campaigns", northId, "v5"] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [targetNorthId, northId, queryClient]);

  const FacebookIcon = () => (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  );

  // Show skeleton loaders while loading
  if (isLoading) {
    return (
      <div className="mb-8 md:mb-12">
        <h3 className="text-xs md:text-sm font-semibold tracking-wider text-foreground mb-4 md:mb-6">
          THE MARKETING MAGIC
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-3 md:gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-background rounded-lg overflow-hidden border border-border shadow-sm">
              <div className="p-2 md:p-3 flex items-center gap-2 border-b border-border">
                <Skeleton className="w-6 h-6 md:w-8 md:h-8 rounded-full" />
                <Skeleton className="h-3 md:h-4 w-24 md:w-32" />
              </div>
              <Skeleton className="aspect-square w-full" />
              <div className="p-2 md:p-3 space-y-2">
                <Skeleton className="h-3 md:h-4 w-full" />
                <Skeleton className="h-3 md:h-4 w-3/4" />
                <Skeleton className="h-2 md:h-3 w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!posts || posts.length === 0) return null;

  return (
    <div className="mb-4 md:mb-6">
      <h3 className={forceMobileLayout ? "text-xs font-semibold tracking-wider text-foreground mb-4" : "text-xs md:text-sm font-semibold tracking-wider text-foreground mb-4 md:mb-6"}>
        THE MARKETING MAGIC
      </h3>
      
        <div className={forceMobileLayout ? "grid grid-cols-1 gap-3" : "grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-3 md:gap-6"}>
        {posts.map((post) => {
          const platform = post.account?.platform || post.platform; // Use post.platform for manual posts
          const accountName = post.account?.account_name || post.account_name;
          const isCaldwellGroup = accountName === "Caldwell Group";
          
          return (
            <div key={post.id} className="bg-background rounded-lg overflow-hidden border border-border shadow-sm">
              {/* Header */}
              <div className="p-2 md:p-3 flex items-center gap-2 border-b border-border">
                <div className="w-6 h-6 md:w-8 md:h-8 rounded-full bg-muted flex items-center justify-center">
                  {platform === "instagram" && <Instagram className="w-3 h-3 md:w-4 md:h-4 text-foreground" />}
                  {platform === "facebook" && <div className="scale-75 md:scale-100"><FacebookIcon /></div>}
                  {platform === "youtube" && <Youtube className="w-3 h-3 md:w-4 md:h-4 text-foreground" />}
                </div>
                <div className="flex-1">
                  <p className="text-xs md:text-sm font-medium text-foreground">
                    {accountName ||
                     // Fallback: try to extract from old posts with prefix format
                     (post.content?.match(/^([^:]+):/) ? post.content.match(/^([^:]+):/)?.[1]?.trim() : null) ||
                     `${platform.charAt(0).toUpperCase() + platform.slice(1)} Post`}
                  </p>
                </div>
              </div>

              {/* Image/Video */}
              <div className="relative aspect-square bg-muted">
                {post.image_url && (
                  <img 
                    src={post.image_url} 
                    alt="Post content"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                )}
                
                {platform === "youtube" && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-[#FF0000] flex items-center justify-center shadow-lg">
                      <Play className="w-6 h-6 md:w-8 md:h-8 text-white fill-white ml-1" />
                    </div>
                  </div>
                )}
                
                {platform === "instagram" && (
                  <div className="absolute bottom-2 right-2 md:bottom-3 md:right-3">
                    <div className="bg-black/60 text-white text-[10px] md:text-xs px-1.5 md:px-2 py-0.5 md:py-1 rounded">
                      Carousel
                    </div>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-2 md:p-3">
                {platform === "youtube" && (
                  <div className="mb-2">
                    <p className="text-xs md:text-sm font-medium text-foreground line-clamp-2">
                      {(() => {
                        if (!post.content) return address;
                        // Split by colon, but only if it's followed by a space (to avoid splitting URLs like https://)
                        const colonIndex = post.content.indexOf(': ');
                        if (colonIndex > 0) {
                          return post.content.substring(colonIndex + 2).trim();
                        }
                        return post.content;
                      })()}
                    </p>
                    <p className="text-[10px] md:text-xs text-muted-foreground mt-1">
                      {post.views_count?.toLocaleString() || 0} views · {format(new Date(post.posted_at), "MMM d, yyyy")}
                    </p>
                  </div>
                )}
                
                {platform !== "youtube" && post.content && (
                  <p className="text-xs md:text-sm text-muted-foreground line-clamp-3">
                    {post.content.replace(/^[^:]+:\s*/, '')}
                  </p>
                )}

                {/* Stats */}
                <div className="flex items-center gap-2 md:gap-4 mt-2 md:mt-3 text-[10px] md:text-xs text-muted-foreground flex-wrap">
                  {isCaldwellGroup ? (
                    // For Caldwell Group, show: Likes, Shares, Reach, Engagement
                    <>
                      <span>{post.likes_count?.toLocaleString() || 0} likes</span>
                      <span>{post.shares_count?.toLocaleString() || 0} shares</span>
                      <span>{post.reach?.toLocaleString() || 0} reach</span>
                      <span>{post.engagement?.toLocaleString() || 0} engagement</span>
                    </>
                  ) : (
                    // For other accounts, show default stats
                    <>
                      {platform === "instagram" && (
                        <>
                          <span>{post.likes_count?.toLocaleString() || 0} likes</span>
                          <span>{post.comments_count?.toLocaleString() || 0} comments</span>
                          {post.account && <span className="hidden md:inline">{post.impressions?.toLocaleString() || 0} impressions</span>}
                        </>
                      )}
                      {platform === "facebook" && (
                        <>
                          <span>{post.likes_count?.toLocaleString() || 0} reactions</span>
                          <span>{post.comments_count?.toLocaleString() || 0} comments</span>
                          {post.account && <span className="hidden md:inline">{post.engagement?.toLocaleString() || 0} engagement</span>}
                        </>
                      )}
                      {platform === "youtube" && (
                        <>
                          <span>{post.likes_count?.toLocaleString() || 0} likes</span>
                          <span>{post.comments_count?.toLocaleString() || 0} comments</span>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SocialCampaigns;
